import jsPDF from 'jspdf'
import 'jspdf-autotable'

const CURRENCY_REGEX = /(₹|Rs\.?|INR|\$|€|£|¥|₨)/gi
const AMOUNT_HEADER_REGEX = /(amount|total|balance|net|grand|price|value|paid|debit|credit|sale|purchase)/i
const SUMMARY_ROW_REGEX = /(total|grand\s*total|opening|closing|balance|net\s*payable|subtotal)/i
const EXPLICIT_TOTAL_ROW_REGEX = /(total|grand\s*total|subtotal)/i

const toText = (cell) => {
  if (cell === null || cell === undefined) return ''
  if (typeof cell === 'object') {
    if ('content' in cell) return String(cell.content ?? '')
    return ''
  }
  return String(cell)
}

const parseNumber = (value) => {
  const text = toText(value)
    .replace(CURRENCY_REGEX, '')
    .replace(/,/g, '')
    .replace(/\s+/g, '')

  if (!text) return null

  const cleaned = text.replace(/[^0-9.-]/g, '')
  if (!cleaned || cleaned === '-' || cleaned === '.' || cleaned === '-.') return null

  const numericValue = Number(cleaned)
  return Number.isFinite(numericValue) ? numericValue : null
}

const normalizeCell = (cell) => {
  if (cell === null || cell === undefined) return cell

  if (typeof cell === 'string') {
    return cell.replace(CURRENCY_REGEX, '').trim()
  }

  if (typeof cell === 'number') {
    return cell
  }

  if (typeof cell === 'object' && cell !== null && 'content' in cell) {
    const content = cell.content
    return {
      ...cell,
      content: typeof content === 'string' ? content.replace(CURRENCY_REGEX, '').trim() : content,
    }
  }

  return cell
}

const normalizeRows = (rows) => {
  if (!Array.isArray(rows)) return rows

  return rows.map((row) => {
    if (!Array.isArray(row)) return row
    return row.map(normalizeCell)
  })
}

const getColumnCount = (options) => {
  if (Array.isArray(options?.head) && Array.isArray(options.head[0])) return options.head[0].length
  if (Array.isArray(options?.columns)) return options.columns.length
  if (Array.isArray(options?.body) && Array.isArray(options.body[0])) return options.body[0].length
  return 0
}

const getHeaderLabels = (options, columnCount) => {
  if (Array.isArray(options?.head) && Array.isArray(options.head[0])) {
    return options.head[0].map((headerCell) => toText(headerCell).toLowerCase())
  }

  if (Array.isArray(options?.columns)) {
    return options.columns.map((col) => {
      if (typeof col === 'string') return col.toLowerCase()
      if (col && typeof col === 'object') {
        return String(col.header ?? col.dataKey ?? '').toLowerCase()
      }
      return ''
    })
  }

  return new Array(columnCount).fill('')
}

const findBestAmountColumn = (body, headers, columnCount) => {
  let bestColumnIndex = -1
  let bestScore = 0

  for (let columnIndex = 0; columnIndex < columnCount; columnIndex += 1) {
    const headerScore = AMOUNT_HEADER_REGEX.test(headers[columnIndex] || '') ? 2 : 0
    let numericCount = 0
    let runningTotal = 0

    for (const row of body) {
      if (!Array.isArray(row)) continue
      const value = parseNumber(row[columnIndex])
      if (value === null) continue
      numericCount += 1
      runningTotal += value
    }

    if (numericCount === 0) continue

    const score = headerScore + numericCount
    if (score > bestScore) {
      bestScore = score
      bestColumnIndex = columnIndex
    }

    if (score === bestScore && headerScore > 0 && Math.abs(runningTotal) > 0) {
      bestColumnIndex = columnIndex
    }
  }

  return bestColumnIndex
}

const buildTotalFooter = (options) => {
  const body = Array.isArray(options?.body) ? options.body : []
  const columnCount = getColumnCount(options)

  if (!columnCount || !body.length) return null

  const headers = getHeaderLabels(options, columnCount)
  const amountColumnIndex = findBestAmountColumn(body, headers, columnCount)
  if (amountColumnIndex < 0) return null

  let totalAmount = 0
  let hasNumericRows = false

  const isSummaryRow = (row) => {
    if (!Array.isArray(row)) return false

    return row.some((cell) => SUMMARY_ROW_REGEX.test(toText(cell).toLowerCase()))
  }

  for (const row of body) {
    if (!Array.isArray(row)) continue
    if (isSummaryRow(row)) continue

    const value = parseNumber(row[amountColumnIndex])
    if (value === null) continue
    hasNumericRows = true
    totalAmount += value
  }

  if (!hasNumericRows) return null

  const footerRow = new Array(columnCount).fill('')
  const labelColumnIndex = amountColumnIndex > 0 ? amountColumnIndex - 1 : 0
  footerRow[labelColumnIndex] = 'Total Amount'
  footerRow[amountColumnIndex] = totalAmount.toFixed(2)

  return [footerRow]
}

const hasExplicitTotalInBody = (options) => {
  const body = Array.isArray(options?.body) ? options.body : []

  return body.some(
    (row) => Array.isArray(row) && row.some((cell) => EXPLICIT_TOTAL_ROW_REGEX.test(toText(cell).toLowerCase()))
  )
}

const shouldAppendFooter = (options) => {
  if (hasExplicitTotalInBody(options)) return false

  if (!Array.isArray(options?.foot) || !options.foot.length) return true

  return !options.foot.some((row) =>
    Array.isArray(row) && row.some((cell) => /total\s*amount/i.test(toText(cell)))
  )
}

const enhancePdfReports = () => {
  if (!jsPDF?.API?.autoTable || jsPDF.API.__chefmatePdfEnhanced) {
    return
  }

  const originalAutoTable = jsPDF.API.autoTable

  jsPDF.API.autoTable = function autoTableWithEnhancements(...args) {
    if (!args.length || typeof args[0] !== 'object' || args[0] === null) {
      return originalAutoTable.apply(this, args)
    }

    const options = args[0]

    const normalizedOptions = {
      ...options,
      head: normalizeRows(options.head),
      body: normalizeRows(options.body),
      foot: normalizeRows(options.foot),
    }

    if (shouldAppendFooter(normalizedOptions)) {
      const generatedFooter = buildTotalFooter(normalizedOptions)
      if (generatedFooter) {
        normalizedOptions.foot = [...(normalizedOptions.foot || []), ...generatedFooter]
        normalizedOptions.showFoot = normalizedOptions.showFoot || 'lastPage'
        normalizedOptions.footStyles = {
          fontStyle: 'bold',
          ...(normalizedOptions.footStyles || {}),
        }
      }
    }

    return originalAutoTable.call(this, normalizedOptions)
  }

  jsPDF.API.__chefmatePdfEnhanced = true
}

enhancePdfReports()

export default enhancePdfReports